<template>
  <div v-if="sortedEntries.length === 0">
    <span class="grey--text text--lighten-1">n/a</span>
  </div>
  <div v-else>
    <div
      v-for="entry in visibleEntries"
      :key="entry.name"
      class="d-flex align-center my-1"
      style="font-size: 11px; line-height: 1.3;"
    >
      <div
        style="flex: 1; min-width: 0;"
      >
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="text-truncate font-weight-medium">{{ entry.name }}</span>
          <span
            class="ml-1 grey--text text--darken-1"
            style="white-space: nowrap;"
          >{{ entry.displayCount }} ({{ entry.percentage }}%)</span>
        </div>
        <div
          v-if="entry.parts && entry.parts.length > 0"
          style="height: 6px; border-radius: 3px; overflow: hidden; background: #eee;"
        >
          <div
            class="d-flex"
            :style="{ width: entry.percentage + '%', height: '100%' }"
          >
            <div
              v-for="part in entry.parts"
              :key="part.key"
              :style="{
                width: entry.numericCount > 0 ? (part.value / entry.numericCount * 100) + '%' : '0%',
                height: '100%',
                backgroundColor: part.color
              }"
            />
          </div>
        </div>
        <v-progress-linear
          v-else
          :value="entry.percentage"
          height="6"
          rounded
          color="primary"
          background-color="grey lighten-3"
        />
        <div
          v-if="entry.parts && entry.parts.length > 0"
          style="font-size: 10px; line-height: 1.4;"
          class="grey--text text--darken-1 mt-1"
        >
          <span
            v-for="(part, idx) in entry.parts"
            :key="'lbl-' + part.key"
          >
            <span v-if="idx > 0"> · </span>
            <span :style="{ color: part.color }">&#9679;</span>
            {{ part.label }}: {{ suffix ? `${part.value} ${suffix}` : part.value }}
          </span>
        </div>
      </div>
    </div>
    <v-tooltip
      v-if="hiddenEntries.length > 0"
      top
      max-width="300"
    >
      <template v-slot:activator="{ on }">
        <div
          class="text-caption grey--text text--darken-1 mt-1"
          style="cursor: pointer"
          v-on="on"
        >
          {{ $t('insights.show_more', { count: hiddenEntries.length }) }}
        </div>
      </template>
      <div>
        <div
          v-for="entry in hiddenEntries"
          :key="entry.name"
          class="my-1"
        >
          {{ entry.name }}: {{ entry.displayCount }} ({{ entry.percentage }}%)
        </div>
      </div>
    </v-tooltip>
  </div>
</template>

<script>
import { getTranslatedTagValue } from '@/utils/helpers'

const MAX_VISIBLE = 5

const PART_COLORS = ['#1976D2', '#F57C00', '#388E3C', '#D32F2F', '#7B1FA2', '#0097A7']

const PART_KEY_COLORS = {
  joker: '#1976D2',
  other: '#F57C00'
}

export default {
  name: 'TagBreakdownCell',
  props: {
    stats: {
      type: Object,
      required: true
    },
    tag: {
      type: Object,
      required: true
    },
    statPrefix: {
      type: String,
      default: 'NUM'
    },
    suffix: {
      type: String,
      default: null
    }
  },
  data: () => ({
    partColors: PART_COLORS
  }),
  computed: {
    tagStatsKey() {
      return `${this.statPrefix}_${this.tag.id}`
    },
    tagData() {
      return this.stats[this.tagStatsKey] || {}
    },
    total() {
      return Object.values(this.tagData).reduce((sum, val) => {
        if (typeof val === 'number') return sum + val
        if (val && typeof val === 'object' && typeof val.total === 'number') return sum + val.total
        return sum
      }, 0)
    },
    sortedEntries() {
      return Object.entries(this.tagData)
        .map(([rawName, count]) => {
          const name = getTranslatedTagValue(rawName)

          const isNested = count && typeof count === 'object' && 'total' in count
          const numericCount = isNested ? count.total : (typeof count === 'number' ? count : 0)

          const formatted = typeof numericCount === 'number' && !Number.isInteger(numericCount)
            ? Math.round(numericCount * 100) / 100
            : numericCount

          let parts = null
          if (isNested) {
            parts = Object.entries(count)
              .filter(([key]) => key !== 'total')
              .map(([key, val], idx) => ({
                key,
                label: key.charAt(0).toUpperCase() + key.slice(1),
                color: PART_KEY_COLORS[key] || PART_COLORS[idx % PART_COLORS.length],
                value: typeof val === 'number'
                  ? (Number.isInteger(val) ? val : Math.round(val * 100) / 100)
                  : 0
              }))
              .filter(p => p.value > 0)
              .sort((a, b) => b.value - a.value)

            if (parts.length === 0) parts = null
          }

          return {
            name,
            count: formatted,
            numericCount,
            displayCount: this.suffix ? `${formatted} ${this.suffix}` : formatted,
            percentage: this.total > 0 ? Math.round((numericCount / this.total) * 100) : 0,
            parts
          }
        })
        .sort((a, b) => b.numericCount - a.numericCount)
    },
    visibleEntries() {
      return this.sortedEntries.slice(0, MAX_VISIBLE)
    },
    hiddenEntries() {
      return this.sortedEntries.slice(MAX_VISIBLE)
    }
  }
}
</script>
